import React from 'react'
import PropTypes from 'prop-types'
import cssModules from 'react-css-modules'
import { Link } from 'react-router'
import { getI18n } from 'client/i18n'
import EditPart from 'components/edit_part'
import StandardEditPart from 'components/standard_edit_part'
import EmptyEditPart from 'components/empty_edit_part'
import Cover from 'components/cover'
import styles from './edit_main_cf.less'
import { glimpseRoute } from 'lib/route_utils'
import { formatMoney, formatDateShort, formatDate } from 'client/griffin'
import { HeadingText } from '@paypalcorp/pp-react'
import Separator from 'components/common/separator'

const MAX_DESCRIPTION_ROWS = 5
const i18n = getI18n('components/edit_glimpse/edit_main')

class EditMain extends React.Component {
  static propTypes = {
    title: PropTypes.string,
    backgroundImage: PropTypes.string,
    gradientColor: PropTypes.string,
    backgroundPan: PropTypes.number,
    goal: PropTypes.number,
    endDate: PropTypes.instanceOf(Date),
    ended: PropTypes.bool,
    description: PropTypes.string,
    contribPrefs: PropTypes.string,
    tracking: PropTypes.string,
    currency: PropTypes.string,
    showDelete: PropTypes.bool,
    location: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    onReopen: PropTypes.func,
    isAndroid: PropTypes.bool,
    categoryName: PropTypes.string,
    category: PropTypes.string,
    postalCode: PropTypes.string,
    maxDays: PropTypes.number,
    maxGoalAmount: PropTypes.string,
  }

  render() {
    const editRoute = glimpseRoute.bind(undefined, 'edit')
    const goalFormatted =
      this.props.goal &&
      formatMoney(this.props.currency, this.props.goal, {
        removeTrailingZeros: true,
      })
    const maxGoalFormatted =
      this.props.maxGoalAmount &&
      formatMoney(this.props.currency, this.props.maxGoalAmount)
    const { categoryName, maxDays } = this.props

    return (
      <div>
        <HeadingText size="md" styleName="title">
          {i18n('edit_title_cf')}
        </HeadingText>
        {this.props.ended && (
          <div styleName="ended-title">
            {i18n('ended', {
              endDate: formatDateShort(this.props.endDate),
            })}
          </div>
        )}
        <Separator type="dotted" />
        {(() => {
          if (this.props.goal) {
            return (
              <StandardEditPart
                targetLocation={editRoute('amount')}
                title={i18n('goal_amount_cf', {
                  maxGoalAmount: maxGoalFormatted,
                })}
                value={goalFormatted}
                key="goal_amount"
                editName="goalAmount"
              />
            )
          }
          return (
            <EmptyEditPart
              title={i18n('goal_amount')}
              description={i18n('add_goal_amount')}
              buttonTitle={i18n('add_goal_amount_button')}
              targetLocation={editRoute('amount')}
              key="empty_goal_amount"
              editName="goalAmount"
            />
          )
        })()}
        {(() => {
          if (this.props.endDate) {
            if (!this.props.ended) {
              return (
                <StandardEditPart
                  targetLocation={editRoute('end_date')}
                  title={i18n('ends_on_cf', { maxDays })}
                  value={formatDate(this.props.endDate, 'yMMdd')}
                  key="ends_on"
                />
              )
            }
            return (
              <StandardEditPart
                editButtonDisplay="hide"
                title={i18n('ends_on_cf', { maxDays })}
                value={i18n('ended', {
                  endDate: formatDate(this.props.endDate, 'yMMdd'),
                })}
                description={i18n('pool_closed')}
                key="ended_ends_on"
                editName="endDate"
              />
            )
          }
          return (
            <EmptyEditPart
              title={i18n('ends_on')}
              description={i18n('add_ends_on')}
              buttonTitle={i18n('add_ends_on_button')}
              targetLocation={editRoute('end_date')}
              key="empty_ends_on"
              editName="endDate"
            />
          )
        })()}

        {this.props.postalCode ? (
          <StandardEditPart
            targetLocation={editRoute('postal_code')}
            title={i18n('postal_code')}
            value={this.props.postalCode}
            key="postal_code"
            editName="postal_code"
          />
        ) : (
          <EmptyEditPart
            title={i18n('postal_code')}
            description={i18n('add_postal_code')}
            buttonTitle={i18n('add_postal_code_button')}
            targetLocation={editRoute('postal_code')}
            key="empty_postal_code"
            editName="postal_code"
          />
        )}

        <div>
          <StandardEditPart
            targetLocation={editRoute('name')}
            title={i18n('name_cf')}
            value={this.props.title}
            key="name"
            editName="poolName"
          />
        </div>

        {this.props.category ? (
          <StandardEditPart
            targetLocation={editRoute('category')}
            title={i18n('category')}
            value={categoryName}
            key="category"
            editName="category"
          />
        ) : (
          <EmptyEditPart
            title={i18n('category')}
            description={i18n('add_category')}
            buttonTitle={i18n('add_category_button')}
            targetLocation={editRoute('category')}
            key="empty_category"
            editName="category"
          />
        )}

        {this.props.description ? (
          <StandardEditPart
            targetLocation={editRoute('description')}
            title={i18n('description_cf')}
            value={this.props.description}
            maxRows={MAX_DESCRIPTION_ROWS}
            key="description"
            editName="description"
          />
        ) : (
          <EmptyEditPart
            title={i18n('description')}
            description={i18n('add_description')}
            buttonTitle={i18n('add_description_button')}
            targetLocation={editRoute('description')}
            key="empty_description"
            editName="description"
          />
        )}
        {!this.props.isAndroid && (
          <EditPart
            targetLocation={editRoute('cover')}
            editName="cover"
            editButtonDisplay="bottom"
            labelName={i18n('goal_cover_cf')}
          >
            <div styleName="goal-cover-title">{i18n('goal_cover_cf')}</div>
            <Cover
              backgroundImage={this.props.backgroundImage}
              backgroundPan={this.props.backgroundPan}
              gradientColor={this.props.gradientColor}
              showHeader={false}
              suppressPrompt={true}
              customStyle={{
                height: '215px',
              }}
            />
          </EditPart>
        )}

        {this.props.showDelete && (
          <Link
            styleName="delete-link"
            data-nemo="deletePool"
            to={editRoute('delete')}
          >
            {i18n('delete_cf')}
          </Link>
        )}
      </div>
    )
  }
}

export default cssModules(EditMain, styles)
